// Reads behind the Linear parts of the admin pages (poc CT-009, module-14 Section 2): whether Linear is
// connected (S8's Linear step, S9's Linear section, S12's Linear tab) and the team and project pickers. The
// pickers are read live from Linear by the server on every call, so they aren't retried here: the server has
// already retried (1 s, 4 s) before it answers 503.
import type { LinearProject, LinearStatus, LinearTeam } from '@feedback/shared/contracts/linear';
import { queryOptions } from '@tanstack/react-query';
import { api, isApiError, seg } from '@/lib/api';
import { errorText } from './admin-api';

const STATUS_STALE_MS = 60_000;
/** The wizard's Review and a Back step reuse what the pickers read, without asking Linear again. */
const PICKER_STALE_MS = 30_000;
const UNREACHABLE = 'We couldn’t reach Linear. Try again in a moment.';
const HTTP_UNAVAILABLE = 503;

export const linearStatusQuery = queryOptions({
  queryKey: ['linear', 'status'],
  queryFn: () => api.get<LinearStatus>('/api/linear/status'),
  staleTime: STATUS_STALE_MS,
});

export const linearTeamsQuery = queryOptions({
  queryKey: ['linear', 'teams'],
  queryFn: async () => (await api.get<{ items: LinearTeam[] }>('/api/linear/teams')).items,
  staleTime: PICKER_STALE_MS,
  retry: false,
});

export const linearProjectsQuery = (teamId: string) =>
  queryOptions({
    queryKey: ['linear', 'teams', teamId, 'projects'],
    queryFn: async () => (await api.get<{ items: LinearProject[] }>(`/api/linear/teams/${seg(teamId)}/projects`)).items,
    staleTime: PICKER_STALE_MS,
    retry: false,
  });

/** "Feedback Sandbox (FBS)" */
export function teamLabel(team: Pick<LinearTeam, 'name' | 'key'>): string {
  return `${team.name} (${team.key})`;
}

/** A failed Linear call as the pages say it: the server's 503 means Linear couldn't be reached (or was slow). */
export function linearErrorText(error: unknown): string {
  return isApiError(error) && error.status === HTTP_UNAVAILABLE ? UNREACHABLE : errorText(error);
}
